import { AxiosError, AxiosResponse } from 'axios';
import { UseQueryOptions } from 'react-query';
import { useCustomMutate, useCustomPostQuery, useCustomQuery } from '../../../../../hooks/query';
import { GenericErrorResponse } from '../../../..';

import {
  getAppealApplicationRecords,
  getAppealAvailableResults,
  getAppealExamListing,
  getAppealFees,
  getAppealReceipt,
  getCandidateDownloadSummary,
  getResitReceiptSummary,
  getReviewDraft,
  postAppealDraft,
  postAppealEligibility,
  postCandidateRegistration,
} from '../api-services/api';
import {
  AppealApplicationRecordsResponse,
  AppealAvailableLevelResponse,
  AppealDownloadSummaryResponse,
  AppealDraftRequestBody,
  AppealDraftResponseBody,
  AppealDraftReviewResponse,
  AppealFeeResponse,
  AppealListingResponse,
  AppealProceedToPaymentRequestBody,
  AppealProceedToPaymentResponseBody,
  AppealSummaryResponse,
  AppealTransactionDetailsInSummary,
  EligibilityResponse,
  ExamLevelCodeRequest,
} from '../../model';
import {
  GET_APPEAL_APPLICATION_RECORDS,
  GET_APPEAL_DRAFT_REVIEW,
  GET_APPEAL_FEES,
  GET_APPEAL_RECEIPT,
  GET_APPEAL_RECEIPT_SUMMARY,
  GET_APPEAL_SUMMARY_PDF,
  GET_AVAILABLE_RESULTS,
  POST_APPEAL_DRAFT,
  POST_APPEAL_ELIGIBILITY_CHECK,
  POST_PROCEED_TO_PAY,
} from './keys';
import { GET_APPEAL_LISTING } from '../api-services/routes';
import { ServiceError } from '../../../../../hooks/error';

/** to get appeal exam level fees
 * */
export const useGetAppealApplicationRecords = (appealUuid?: string) =>
  useCustomQuery<AppealApplicationRecordsResponse>(GET_APPEAL_APPLICATION_RECORDS, () =>
    getAppealApplicationRecords(appealUuid).then((response) => response.data)
  );

/** to get appeal exam level fees
 * */
export const useGetAppealFees = () =>
  useCustomQuery<AppealFeeResponse>(GET_APPEAL_FEES, () =>
    getAppealFees().then((response) => response.data)
  );

/** to get available exams results to appeal
 * */
export const useGetAvailableResult = (token: string) =>
  useCustomQuery<AppealAvailableLevelResponse>(GET_AVAILABLE_RESULTS, () =>
    getAppealAvailableResults(token).then((response) => response.data)
  );

/** to get available exam listings for a particular exam level
 * */
export const useGetExamListing = (series: string, year: string, code: string, token: string) =>
  useCustomQuery<AppealListingResponse>(GET_APPEAL_LISTING, () =>
    getAppealExamListing(series, year, code, token).then((response) => response.data)
  );

/** to post draft of appeal
 * */
export const usePostAppealDraft = (
  successCallback: (res: any) => void,
  failCallback: (res: any) => void
) =>
  useCustomMutate<
    AxiosResponse<AppealDraftResponseBody>,
    AppealDraftRequestBody,
    AxiosError<GenericErrorResponse>
  >(
    [POST_APPEAL_DRAFT],
    postAppealDraft,
    {
      onSuccess: successCallback,
      onError: failCallback,
    },
    false,
    false
  );

/** get details of draft appeal for appeal
 * */
export const useGetReviewDraft = (applicationId: string) =>
  useCustomQuery<AppealDraftReviewResponse>(GET_APPEAL_DRAFT_REVIEW, () =>
    getReviewDraft(applicationId).then((response) => response.data)
  );

/** to get appeal receipt summary
 * */
export const useGetAppealReceipt = (internalTransactionRef = '') =>
  useCustomQuery<AppealTransactionDetailsInSummary>(GET_APPEAL_RECEIPT, () =>
    getAppealReceipt(internalTransactionRef).then((response) => response.data)
  );

/** to get appeal receipt summary
 * */
export const useGetAppealReceiptSummary = (uuid = '') =>
  useCustomQuery<AppealSummaryResponse>(GET_APPEAL_RECEIPT_SUMMARY, () =>
    getResitReceiptSummary(uuid).then((response) => response.data)
  );

/**
 * To retrieve download pre-signed url for download resit summary for candidate
 * @param internalTransactionRef internal transaction ref to search
 * @returns
 */
export const useCandidateDownloadSummary = (
  onSuccess: (download: AppealDownloadSummaryResponse) => void,
  onError: (data: AxiosError) => void,
  uuid = ''
) =>
  useCustomQuery(
    GET_APPEAL_SUMMARY_PDF,
    () => getCandidateDownloadSummary(uuid).then((res) => res.data),
    {
      enabled: false,
      onSuccess,
      onError,
    },
    true,
    false
  );

/** to create candidate after payment successful
 * @return => GetResitSubject list
 * */
export const usePostAppealProceedToPayment = (
  successCallback: (res: any) => void,
  failCallback: (res: any) => void
) =>
  useCustomMutate<
    AxiosResponse<AppealProceedToPaymentResponseBody>,
    AppealProceedToPaymentRequestBody,
    AxiosError<GenericErrorResponse>
  >(
    POST_PROCEED_TO_PAY,
    postCandidateRegistration,
    {
      onSuccess: (res) => successCallback(res),
      onError: (err) => failCallback(err),
    },
    false,
    false
  );

/**
 * to check eligibility for appeal
 */
export const useAppealEligibilityQuery = (
  payload: ExamLevelCodeRequest,
  queryOptions: UseQueryOptions<
    AxiosResponse<EligibilityResponse>,
    ServiceError,
    AxiosResponse<EligibilityResponse>
  >
) =>
  useCustomPostQuery(
    [POST_APPEAL_ELIGIBILITY_CHECK, payload],
    (csrf: string) => postAppealEligibility(csrf, payload).then((res) => res),
    queryOptions,
    payload,
    false,
    false
  );
